import React, { useEffect, useState } from 'react';
import { apiConstants, post, postGeneric } from '../../common/constants';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import { Button, Card, CardContent, Grid, Typography, Box } from '@mui/material';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import Loader from '../../common/Loader';
import _ from 'lodash';
import { useForm } from 'react-hook-form';
import { FormProvider, RHFSearchableSelectField } from '../../common/hook-form';
import { formatCurrency } from '../../helpers/UtilityHelper';
import { formatDate } from '../../common/util';
import { WALLET_TYPE_OPTIONS, WalletTypeChip } from './walletUi';

const SNAPSHOT_ACTION = 'WALLET_SWITCH_SNAPSHOT';

const amountCell = (v) => (v == null ? '—' : formatCurrency(v));

/**
 * Wallet switch history — renders WALLET_SWITCH_SNAPSHOT rows from merchant_wallet_activity_log.
 * These rows are written by the backend on every real Prepaid<->Postpaid switch and carry the
 * outgoing wallet's closing balances (the money-at-risk number finance reconciles against).
 *
 * Props:
 *  - merchantId: optional — scope to one merchant (used inside Wallet Config)
 *  - compact: smaller embedded rendering, no merchant column
 */
export default function WalletSwitchHistory({ merchantId = null, compact = false }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [merchantNames, setMerchantNames] = useState({});
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(compact ? 5 : 20);
    const [merchantFilter, setMerchantFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');

    const methods = useForm({ defaultValues: { merchant: '', walletType: '' } });
    const { reset } = methods;

    useEffect(() => {
        if (compact) return; // embedded view is merchant-scoped; names not needed
        post(apiConstants.MERCHANT_SEARCH_CACHED, {
            offset: 0, limit: 10000, andfilter: [], includes: 'name,id'
        }).then((res) => {
            const map = {};
            (res.data?.data || []).forEach((m) => { map[m.id] = m.name; });
            setMerchantNames(map);
        }).catch(() => { /* names are cosmetic; the grid still shows ids */ });
    }, [compact]);

    useEffect(() => {
        setLoading(true);
        const andfilter = [{ field: 'action', operator: 'eq', value: SNAPSHOT_ACTION }];
        if (merchantId) andfilter.push({ field: 'merchantId', operator: 'eq', value: merchantId });
        postGeneric(apiConstants.REPORT_GENERIC_SEARCH, {
            collection: 'merchant_wallet_activity_log',
            limit: 10000,
            andfilter,
        }).then((res) => {
            const sorted = _.orderBy(res?.data?.data || [], ['createdAt'], ['desc']);
            setRows(sorted);
        }).catch((err) => {
            console.error(err);
            setRows([]);
        }).finally(() => setLoading(false));
    }, [merchantId]);

    const columns = [
        {
            field: 'createdAt', headerName: 'Switched On', flex: 1, minWidth: 150,
            renderCell: ({ row }) => formatDate(row.createdAt),
        },
        ...(compact ? [] : [{
            field: 'merchantId', headerName: 'Merchant', flex: 1.4, minWidth: 200,
            renderCell: ({ row }) => merchantNames[row.merchantId] || row.merchantId,
        }]),
        {
            field: 'switch', headerName: 'Switch', flex: 1.4, minWidth: 210, sortable: false,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WalletTypeChip type={row.switchSnapshot?.fromWalletType} />
                    <ArrowRightAltIcon fontSize="small" />
                    <WalletTypeChip type={row.switchSnapshot?.toWalletType} />
                </Box>
            ),
        },
        {
            field: 'closingBalance', headerName: 'Closing Balance', flex: 1, minWidth: 130, align: 'right', headerAlign: 'right',
            renderCell: ({ row }) => {
                const v = row.switchSnapshot?.fromClosingWalletBalance;
                if (v == null) return '—';
                return (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: v < 0 ? '#dc3545' : (v > 0 ? '#28a745' : 'inherit') }}>
                        {formatCurrency(v)}
                    </Typography>
                );
            },
        },
        {
            field: 'closingDue', headerName: 'Closing Due', flex: 1, minWidth: 110, align: 'right', headerAlign: 'right',
            renderCell: ({ row }) => amountCell(row.switchSnapshot?.fromClosingDueAmount),
        },
        {
            field: 'openingDue', headerName: 'Opening Due', flex: 1, minWidth: 110, align: 'right', headerAlign: 'right',
            renderCell: ({ row }) => amountCell(row.switchSnapshot?.toOpeningDueAmount),
        },
        {
            field: 'loggedInUser', headerName: 'Actioned By', flex: 1.2, minWidth: 170,
            renderCell: ({ row }) => row.loggedInUser || 'system',
        },
    ];

    if (compact && rows.length === 0 && !loading) {
        return <Typography variant="caption" color="text.secondary">No wallet switches recorded for this merchant.</Typography>;
    }

    // wallet-type filter matches the switched-TO type (what the merchant became)
    const filteredRows = rows.filter((r) =>
        (!merchantFilter || r.merchantId === merchantFilter) &&
        (!typeFilter || r.switchSnapshot?.toWalletType === typeFilter)
    );

    // dropdown lists only merchants that actually have a switch recorded
    const merchantOptions = _.orderBy(
        _.uniqBy(rows.map((r) => ({ value: r.merchantId, label: merchantNames[r.merchantId] || r.merchantId })), 'value'),
        'label'
    );

    const onSubmit = (data) => {
        setMerchantFilter(data.merchant || '');
        setTypeFilter(data.walletType || '');
        setPage(0);
    };

    return (
        <>
            {loading && <Loader />}
            {!compact && (
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
                            <Grid container spacing={1} sx={{ display: 'flex', justifyContent: 'left' }}>
                                <Grid item xs={12} md={5}>
                                    <RHFSearchableSelectField
                                        width="100%"
                                        name="merchant"
                                        label="Select Merchant"
                                        options={merchantOptions}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <RHFSearchableSelectField
                                        width="100%"
                                        name="walletType"
                                        label="Switched To (Wallet Type)"
                                        options={WALLET_TYPE_OPTIONS}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Button type="submit" variant="contained" color="primary" size="small" sx={{ mt: 2 }}>
                                        Apply Filters
                                    </Button>
                                    <Button variant="contained" color="error" size="small" sx={{ mt: 2, ml: 1 }}
                                        onClick={() => {
                                            setMerchantFilter('');
                                            setTypeFilter('');
                                            setPage(0);
                                            reset({ merchant: '', walletType: '' });
                                        }}>
                                        Reset
                                    </Button>
                                </Grid>
                            </Grid>
                        </FormProvider>
                    </CardContent>
                </Card>
            )}
            <ProzoDataGrid
                autoHeight={true}
                columns={columns}
                rows={filteredRows}
                getRowId={(row) => `${row.merchantId}_${row.createdAt}_${row.switchSnapshot?.toWalletId || ''}`}
                sizePerPage={sizePerPage}
                setSizePerPage={setSizePerPage}
                setPage={setPage}
                page={page}
                paginationMode="client"
                rowCount={filteredRows.length}
            />
        </>
    );
}
